/**
 * RegulationsTable Component
 * 重构后的法规表格主组件
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  DataGridPremium,
  type GridCallbackDetails,
  type GridFilterModel,
  type GridRowModesModel,
  type GridColumnVisibilityModel,
  type GridDensity,
  type GridSortModel,
  type GridPaginationModel,
  GridLogicOperator,
  useGridApiRef,
  type GridSlotProps,
} from "@mui/x-data-grid-premium";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/en";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  useAppStore,
  selectRegulationsListPagination,
  selectRegulationsListMarkets,
  selectRegulationsListProductTypes,
  selectRegulationsListHA,
  selectRegulationsListTags,
  selectRegulationsListTitle,
  selectRegulationsListFromDateIso,
  selectRegulationsListToDateIso,
  selectRegulationsListFavoriteFilter,
  selectRegulationsListActiveSavedFilterId,
  selectRegulationsListGridColumnFilterModel,
  selectRegulationsListGridColumnVisibilityModel,
  selectRegulationsListGridColumnOrderedFields,
  selectRegulationsListGridDensity,
  selectSelectedRequirementIds,
} from "../../stores";
import { RegulationsTableToolbar } from "./Toolbar";
import { FilterBar } from "./FilterBar";
import { SHOW_FAVORITE_PRESET_FILTER_ID } from "./FilterBar/useFilterBar";
import {
  useRegulationsData,
  useFieldsConfig,
  useTableColumns,
  buildDefaultListColumnVisibilityModel,
  filterListTableFieldsForColumnFilterPanel,
  LIST_TABLE_COLUMN_VISIBILITY_LOCKED_FIELDS,
  type StartManualAssessmentFromTablePayload,
} from "./hooks";
import { getListTableManageColumnLabel, mergeListTableColumnOrder, mergeHiddenDefaultsForNewListColumns } from "./hooks/listTableColumnConfig";
import { useHasPrivilege } from "../../hooks/useHasPrivilege";
import { useGlobalConfig } from "../../hooks/useGlobalConfig";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
  regulationsConditionFromGridFilterItem,
  gridFilterConditionsCacheKey,
  type ConceptValue,
} from "../../services/regulationsService";
import { REGULATIONS_GRID_FILTER_LOCALE_TEXT } from "./utils/gridFilterOperatorsFromFieldConfig";
import { useBrandThemeColors } from "../../branding";
import { DataGridManageColumnsPanel } from "../shared/DataGridManageColumnsPanel";
import { RegulationsGridFilterPanel } from "./RegulationsGridFilterPanel";
import { RegulationsGridPanel } from "./RegulationsGridPanel";
import noDataAnim from "../../assets/no-data-anim.webp";
import styles from "./RegulationsTable.module.css";
import { REGULATIONS_TABLE_BASE_CHECKBOX_SLOT_PROPS } from "./RegulationsTableCheckboxIcons";
import { InsightsViewToggle } from "./InsightsViewToggle";
import { RegulationsCardView } from "./RegulationsCardView";
import { toggleRegulationSelection } from "./toggleRegulationSelection";
import {
  readInsightsLandingView,
  writeInsightsLandingView,
  type InsightsLandingView,
} from "./insightsLandingView";

/** Empty grid state — same asset and copy pattern as GlobalSearchPage / other no-data views */
function RegulationsTableNoRowsOverlay() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 280,
        py: 4,
        boxSizing: "border-box",
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="img"
        src={noDataAnim}
        alt=""
        sx={{ width: 160, height: "auto", mb: 2 }}
      />
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
        No search results found.
      </Typography>
    </Box>
  );
}

export interface RegulationsTableProps {
  /** Domain for API header (from cookie on load, passed from HomePage). */
  domain: string;
  /**
   * When false, skip list fetch (e.g. wait until domain view privileges resolve
   * so we do not query the default MPR domain the user cannot view).
   */
  domainReady?: boolean;
  onCompanyScopeImpact?: () => void;
  onCustomScopeImpact?: () => void;
  /** Regulation ids with Company Scope in flight — only those rows show Run AI Assessment loading. */
  companyScopeLoadingRegulationIds?: readonly string[];
  /** Single-row Company Scope (same flow as toolbar bulk). */
  onRowCompanyScopeImpact?: (regulationId: string) => void;
  /** Single-row Custom Scope plan dialog (same flow as toolbar bulk). */
  onRowCustomScopeImpact?: (regulationId: string) => void;
  /** Opens Start Manual Assessment (replaces legacy iframe Associate Process). */
  onStartManualAssessment?: (
    payload: StartManualAssessmentFromTablePayload,
  ) => void;
  /** Increment to re-fetch the regulations list (e.g. after assessment completes). */
  listRefreshSignal?: number;
}


function normalizeColumnVisibilityModel(
  model: GridColumnVisibilityModel,
): GridColumnVisibilityModel {
  const normalized = { ...model };
  LIST_TABLE_COLUMN_VISIBILITY_LOCKED_FIELDS.forEach((field) => {
    normalized[field] = true;
  });
  return normalized;
}

function columnVisibilityModelHasHiddenColumns(
  model: GridColumnVisibilityModel,
): boolean {
  return Object.values(model).some((visible) => visible === false);
}

/** Defer until DataGrid has published apiRef (avoids subscribeEvent on null api). */
function runWhenGridApiReady(
  apiRef: ReturnType<typeof useGridApiRef>,
  fn: () => void,
) {
  queueMicrotask(() => {
    if (!apiRef.current) {
      return;
    }
    fn();
  });
}

const RegulationsTable = ({
  domain,
  domainReady = true,
  onCompanyScopeImpact,
  onCustomScopeImpact,
  companyScopeLoadingRegulationIds = [],
  onRowCompanyScopeImpact,
  onRowCustomScopeImpact,
  onStartManualAssessment,
  listRefreshSignal,
}: RegulationsTableProps) => {
  const apiRef = useGridApiRef();
  const { primaryColor } = useBrandThemeColors();
  const [landingView, setLandingView] = useState<InsightsLandingView>(() =>
    readInsightsLandingView(),
  );
  const handleLandingViewChange = useCallback((view: InsightsLandingView) => {
    setLandingView(view);
    writeInsightsLandingView(view);
  }, []);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const columnVisibilityModel = useAppStore(
    selectRegulationsListGridColumnVisibilityModel,
  );
  const setRegulationsListGridColumnVisibilityModel = useAppStore(
    (s) => s.setRegulationsListGridColumnVisibilityModel,
  );
  const storedColumnOrderedFields = useAppStore(
    selectRegulationsListGridColumnOrderedFields,
  );
  const setRegulationsListGridColumnOrderedFields = useAppStore(
    (s) => s.setRegulationsListGridColumnOrderedFields,
  );
  const gridDensity = useAppStore(selectRegulationsListGridDensity);
  const setRegulationsListGridDensity = useAppStore(
    (s) => s.setRegulationsListGridDensity,
  );

  /** DataGrid may reset column visibility on mount; do not wipe persisted store. */
  const ignoreSpuriousEmptyColumnVisibilityRef = useRef(true);
  const isRestoringGridColumnVisibilityRef = useRef(false);
  const manageColumnsPanelScrollTopRef = useRef(0);

  const setColumnVisibilityModel = useCallback(
    (update: React.SetStateAction<GridColumnVisibilityModel>) => {
      if (isRestoringGridColumnVisibilityRef.current) {
        return;
      }

      const stored =
        useAppStore.getState().regulationsList.gridColumnVisibilityModel;
      const next =
        typeof update === "function" ? update(stored) : update;
      const normalized = normalizeColumnVisibilityModel(next);

      if (
        ignoreSpuriousEmptyColumnVisibilityRef.current &&
        Object.keys(normalized).length === 0 &&
        columnVisibilityModelHasHiddenColumns(stored)
      ) {
        return;
      }
      ignoreSpuriousEmptyColumnVisibilityRef.current = false;
      setRegulationsListGridColumnVisibilityModel(normalized);
    },
    [setRegulationsListGridColumnVisibilityModel],
  );

  const handleDensityChange = useCallback(
    (density: GridDensity) => {
      setRegulationsListGridDensity(density);
    },
    [setRegulationsListGridDensity],
  );

  // Check for regulation read permission
  const hasReadPermission = useHasPrivilege("gri.regulation.read");
  const privilegesLoaded = useAppStore((state) => state.ui?.privilegesLoaded) || false;

  const setSelectedIds = useAppStore(
    (state) => state.setSelectedRequirementIds
  );

  // Pagination and filters from store (persist across detail → home navigation)
  const paginationModel = useAppStore(selectRegulationsListPagination);
  const setPaginationModel = useAppStore(
    (state) => state.setRegulationsListPagination
  );
  const selectedMarkets = useAppStore(
    selectRegulationsListMarkets
  ) as ConceptValue[];
  const selectedProductTypes = useAppStore(
    selectRegulationsListProductTypes
  ) as ConceptValue[];
  const selectedHA = useAppStore(
    selectRegulationsListHA
  ) as ConceptValue[];
  const selectedTags = useAppStore(
    selectRegulationsListTags
  ) as ConceptValue[];
  const setRegulationsListMarkets = useAppStore(
    (state) => state.setRegulationsListMarkets
  );
  const setRegulationsListProductTypes = useAppStore(
    (state) => state.setRegulationsListProductTypes
  );
  const setRegulationsListHA = useAppStore(
    (state) => state.setRegulationsListHA
  );
  const setRegulationsListTags = useAppStore(
    (state) => state.setRegulationsListTags
  );
  const selectedTitle = useAppStore(selectRegulationsListTitle);
  const selectedRequirementIds = useAppStore(selectSelectedRequirementIds);
  const setRegulationsListTitle = useAppStore(
    (state) => state.setRegulationsListTitle
  );
  const fromDateIso = useAppStore(selectRegulationsListFromDateIso);
  const toDateIso = useAppStore(selectRegulationsListToDateIso);
  const setRegulationsListDateRange = useAppStore(
    (state) => state.setRegulationsListDateRange
  );
  const activeSavedFilterId = useAppStore(selectRegulationsListActiveSavedFilterId);
  const favoriteFilter = useAppStore(selectRegulationsListFavoriteFilter);
  const setRegulationsListFavoriteFilter = useAppStore(
    (state) => state.setRegulationsListFavoriteFilter
  );
  const setRegulationsListActiveSavedFilterId = useAppStore(
    (state) => state.setRegulationsListActiveSavedFilterId
  );
  // Convert ISO strings to Dayjs objects
  const fromDate = useMemo<Dayjs | null>(
    () => (fromDateIso ? dayjs(fromDateIso) : null),
    [fromDateIso]
  );
  const toDate = useMemo<Dayjs | null>(
    () => (toDateIso ? dayjs(toDateIso) : null),
    [toDateIso]
  );

  // Load fields configuration
  const { fieldsConfig, loading: fieldsConfigLoading } = useFieldsConfig("list");
  const { config } = useGlobalConfig();
  const { showMessage } = useSnackbar();
  const maxRegulationsPerBatch = config?.max_regulations_per_batch ?? 5;

  /** Legacy Show Favorite preset still loads GET /regulations/favorites. */
  const favoritesListMode =
    favoriteFilter == null &&
    activeSavedFilterId === SHOW_FAVORITE_PRESET_FILTER_ID;

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const filterModel = useAppStore(selectRegulationsListGridColumnFilterModel);
  const setRegulationsListGridColumnFilterModel = useAppStore(
    (s) => s.setRegulationsListGridColumnFilterModel,
  );
  /** DataGrid may emit an empty filterModel on mount/column init; do not wipe persisted store. */
  const ignoreSpuriousEmptyGridFilterRef = useRef(true);
  const isRestoringGridFilterRef = useRef(false);

  useEffect(() => {
    ignoreSpuriousEmptyGridFilterRef.current = true;
    ignoreSpuriousEmptyColumnVisibilityRef.current = true;
    return () => {
      ignoreSpuriousEmptyGridFilterRef.current = true;
      ignoreSpuriousEmptyColumnVisibilityRef.current = true;
    };
  }, []);

  const filledGridColumnFilterCount = useMemo(
    () =>
      filterModel.items.filter(
        (item) => regulationsConditionFromGridFilterItem(item) != null,
      ).length,
    [filterModel.items],
  );

  const showGridFilterToolbarButton =
    !favoritesListMode && filledGridColumnFilterCount > 0;

  useEffect(() => {
    if (favoritesListMode) {
      setSortModel([]);
      setRegulationsListGridColumnFilterModel({
        items: [],
        logicOperator: GridLogicOperator.And,
      });
      queueMicrotask(() => {
        apiRef.current?.hideFilterPanel?.();
      });
    }
  }, [favoritesListMode, setRegulationsListGridColumnFilterModel]);

  // Clear row selection when user switches saved/preset filter chip (not on first mount / hydrate).
  const prevActiveSavedFilterIdRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (prevActiveSavedFilterIdRef.current === undefined) {
      prevActiveSavedFilterIdRef.current = activeSavedFilterId;
      return;
    }
    if (prevActiveSavedFilterIdRef.current !== activeSavedFilterId) {
      setSelectedIds([]);
      queueMicrotask(() => {
        apiRef.current?.setRowSelectionModel({
          type: "include",
          ids: new Set(),
        });
      });
    }
    prevActiveSavedFilterIdRef.current = activeSavedFilterId;
  }, [activeSavedFilterId, setSelectedIds]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      // Zustand setter replaces state; do not pass a React-style updater function.
      const pm = useAppStore.getState().regulationsList.paginationModel;
      setPaginationModel({ ...pm, page: 0 });
    },
    [setPaginationModel],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel, details?: GridCallbackDetails<"filter">) => {
      if (isRestoringGridFilterRef.current) {
        return;
      }

      const stored =
        useAppStore.getState().regulationsList.gridColumnFilterModel;
      if (
        ignoreSpuriousEmptyGridFilterRef.current &&
        model.items.length === 0 &&
        stored.items.length > 0 &&
        details?.reason !== "removeAllFilterItems" &&
        details?.reason !== "deleteFilterItem"
      ) {
        return;
      }
      ignoreSpuriousEmptyGridFilterRef.current = false;

      const prevConditionsKey = gridFilterConditionsCacheKey(stored);
      const nextConditionsKey = gridFilterConditionsCacheKey(model);

      setRegulationsListGridColumnFilterModel({
        ...model,
        logicOperator: GridLogicOperator.And,
      });

      if (prevConditionsKey !== nextConditionsKey) {
        const pm = useAppStore.getState().regulationsList.paginationModel;
        setPaginationModel({ ...pm, page: 0 });
      }

      if (details?.reason === "removeAllFilterItems") {
        queueMicrotask(() => {
          apiRef.current?.hideFilterPanel?.();
        });
      }
    },
    [setRegulationsListGridColumnFilterModel, setPaginationModel],
  );

  // Close filter panel only when all filter rows are removed (not when value is cleared but row remains).
  const prevGridColumnFilterItemsCountRef = useRef(0);
  useEffect(() => {
    const len = filterModel.items.length;
    if (prevGridColumnFilterItemsCountRef.current > 0 && len === 0) {
      queueMicrotask(() => {
        apiRef.current?.hideFilterPanel?.();
      });
    }
    prevGridColumnFilterItemsCountRef.current = len;
  }, [filterModel.items.length]);

  // Load regulations data (must run before columns so favorite updates can be wired)
  const { rows, loading, totalRows, loadError, updateRowFavorite, loadData } =
    useRegulationsData({
    paginationModel,
    sortModel,
    filterModel,
    fieldsConfig,
    selectedMarkets,
    selectedProductTypes,
    selectedHA,
    selectedTags,
    selectedTitle,
    fromDate,
    toDate,
    favoriteFilter,
    domain,
    enabled: domainReady,
    favoritesListMode,
  });

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
    },
    [setPaginationModel],
  );

  useEffect(() => {
    if (!loadError) return;
    showMessage(loadError, "error");
  }, [loadError, showMessage]);

  useEffect(() => {
    if (listRefreshSignal == null || listRefreshSignal === 0) return;
    void loadData();
  }, [listRefreshSignal, loadData]);

  const handleToggleCardSelected = useCallback(
    (regulationId: string, checked: boolean) => {
      const { next, limitReached } = toggleRegulationSelection(
        selectedRequirementIds ?? [],
        regulationId,
        checked,
        maxRegulationsPerBatch,
      );
      if (limitReached) {
        showMessage(
          `You can only select up to ${maxRegulationsPerBatch} regulations.`,
          "warning",
        );
        return;
      }
      setSelectedIds(next);
    },
    [
      selectedRequirementIds,
      setSelectedIds,
      maxRegulationsPerBatch,
      showMessage,
    ],
  );

  const handleBulkFavoritesSuccess = useCallback(
    (regulationIds: string[], isFavorite = true) => {
      regulationIds.forEach((id) => updateRowFavorite(id, isFavorite));
    },
    [updateRowFavorite],
  );

  // Generate columns dynamically
  const { columns, orderedRowKeys } = useTableColumns(
    fieldsConfig,
    maxRegulationsPerBatch,
    updateRowFavorite,
    onStartManualAssessment,
    onRowCompanyScopeImpact,
    onRowCustomScopeImpact,
    companyScopeLoadingRegulationIds,
    sortModel,
  );

  const isGridColumnsReady = !fieldsConfigLoading && columns.length > 0;

  useEffect(() => {
    if (isGridColumnsReady) {
      return;
    }
    queueMicrotask(() => {
      apiRef.current?.hideFilterPanel?.();
    });
  }, [isGridColumnsReady, apiRef]);

  const gridOrderedFields = useMemo(
    () =>
      mergeListTableColumnOrder(
        ["__check__", ...orderedRowKeys, "actions"],
        storedColumnOrderedFields,
      ),
    [orderedRowKeys, storedColumnOrderedFields],
  );

  const gridFilterPanelFilterColumns = useCallback(
    ({ columns }: { columns: ReadonlyArray<{ field?: string }> }) =>
      filterListTableFieldsForColumnFilterPanel(columns, columnVisibilityModel),
    [columnVisibilityModel],
  );

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={manageColumnsPanelScrollTopRef}
        lockedFields={LIST_TABLE_COLUMN_VISIBILITY_LOCKED_FIELDS}
        getColumnLabel={getListTableManageColumnLabel}
      />
    ),
    [columnVisibilityModel, setColumnVisibilityModel],
  );

  const gridSlots = useMemo(
    () => ({
      toolbar: RegulationsTableToolbar,
      noRowsOverlay: RegulationsTableNoRowsOverlay,
      panel: RegulationsGridPanel,
      filterPanel: RegulationsGridFilterPanel,
      columnsManagement: columnsManagementSlot,
    }),
    [columnsManagementSlot],
  );

  // Apply default / persisted column order (checkbox first, Actions last).
  // Do not hard-reset to the catalog order on every columns identity change —
  // that would undo user drag-reorder.
  useEffect(() => {
    if (!isGridColumnsReady || favoritesListMode) {
      return;
    }
    runWhenGridApiReady(apiRef, () => {
      const api = apiRef.current!;
      gridOrderedFields.forEach((field, index) => {
        try {
          api.setColumnIndex(field, index);
        } catch {
          // Column may not be mounted yet on first paint.
        }
      });
    });
  }, [isGridColumnsReady, favoritesListMode, gridOrderedFields, apiRef]);

  const handleColumnOrderChange = useCallback(() => {
    queueMicrotask(() => {
      const fields = apiRef.current?.getAllColumns?.().map((col) => col.field);
      if (!fields?.length) return;
      const next = mergeListTableColumnOrder(fields, fields);
      const prev =
        useAppStore.getState().regulationsList.gridColumnOrderedFields;
      if (
        prev.length === next.length &&
        prev.every((field, index) => field === next[index])
      ) {
        return;
      }
      setRegulationsListGridColumnOrderedFields(next);
    });
  }, [apiRef, setRegulationsListGridColumnOrderedFields]);

  // Apply default hidden columns (Modified Date, Synopsis, etc.) when user has not customized.
  useEffect(() => {
    if (!isGridColumnsReady || favoritesListMode) {
      return;
    }

    const stored =
      useAppStore.getState().regulationsList.gridColumnVisibilityModel;
    if (
      columnVisibilityModelHasHiddenColumns(stored) ||
      Object.keys(stored).length > 0
    ) {
      return;
    }

    const defaults = buildDefaultListColumnVisibilityModel(
      columns.map((col) => col.field),
      fieldsConfig,
    );
    if (Object.keys(defaults).length === 0) {
      return;
    }

    const normalized = normalizeColumnVisibilityModel(defaults);
    setRegulationsListGridColumnVisibilityModel(normalized);

    runWhenGridApiReady(apiRef, () => {
      isRestoringGridColumnVisibilityRef.current = true;
      ignoreSpuriousEmptyColumnVisibilityRef.current = true;
      apiRef.current!.setColumnVisibilityModel(normalized);
      queueMicrotask(() => {
        isRestoringGridColumnVisibilityRef.current = false;
        ignoreSpuriousEmptyColumnVisibilityRef.current = false;
      });
    });
  }, [
    isGridColumnsReady,
    favoritesListMode,
    columns,
    fieldsConfig,
    apiRef,
    setRegulationsListGridColumnVisibilityModel,
  ]);

  // Newly appeared fields API columns: apply default visibility without wiping user prefs.
  useEffect(() => {
    if (!isGridColumnsReady || favoritesListMode) {
      return;
    }

    const stored =
      useAppStore.getState().regulationsList.gridColumnVisibilityModel;
    const merged = mergeHiddenDefaultsForNewListColumns(
      columns.map((col) => col.field),
      stored,
      fieldsConfig,
    );
    if (!merged) {
      return;
    }

    const normalized = normalizeColumnVisibilityModel(merged);
    setRegulationsListGridColumnVisibilityModel(normalized);

    runWhenGridApiReady(apiRef, () => {
      isRestoringGridColumnVisibilityRef.current = true;
      ignoreSpuriousEmptyColumnVisibilityRef.current = true;
      apiRef.current!.setColumnVisibilityModel(normalized);
      queueMicrotask(() => {
        isRestoringGridColumnVisibilityRef.current = false;
        ignoreSpuriousEmptyColumnVisibilityRef.current = false;
      });
    });
  }, [
    isGridColumnsReady,
    favoritesListMode,
    columns,
    fieldsConfig,
    apiRef,
    setRegulationsListGridColumnVisibilityModel,
  ]);

  // Re-apply persisted column filters after remount (detail → list) once columns exist.
  useEffect(() => {
    if (!isGridColumnsReady || favoritesListMode) {
      return;
    }

    const stored =
      useAppStore.getState().regulationsList.gridColumnFilterModel;
    if (stored.items.length === 0) {
      ignoreSpuriousEmptyGridFilterRef.current = false;
      return;
    }

    runWhenGridApiReady(apiRef, () => {
      isRestoringGridFilterRef.current = true;
      ignoreSpuriousEmptyGridFilterRef.current = true;
      apiRef.current!.setFilterModel({
        ...stored,
        logicOperator: GridLogicOperator.And,
      });
      queueMicrotask(() => {
        isRestoringGridFilterRef.current = false;
        ignoreSpuriousEmptyGridFilterRef.current = false;
      });
    });
  }, [isGridColumnsReady, favoritesListMode, apiRef]);

  // Re-apply persisted column visibility after remount (detail → list).
  useEffect(() => {
    if (!isGridColumnsReady || favoritesListMode) {
      return;
    }

    const stored =
      useAppStore.getState().regulationsList.gridColumnVisibilityModel;
    if (
      !columnVisibilityModelHasHiddenColumns(stored) &&
      Object.keys(stored).length === 0
    ) {
      ignoreSpuriousEmptyColumnVisibilityRef.current = false;
      return;
    }

    runWhenGridApiReady(apiRef, () => {
      isRestoringGridColumnVisibilityRef.current = true;
      ignoreSpuriousEmptyColumnVisibilityRef.current = true;
      apiRef.current!.setColumnVisibilityModel(stored);
      queueMicrotask(() => {
        isRestoringGridColumnVisibilityRef.current = false;
        ignoreSpuriousEmptyColumnVisibilityRef.current = false;
      });
    });
  }, [isGridColumnsReady, favoritesListMode, apiRef]);

  // Force pinning when columns or rows load
  useEffect(() => {
    if (!isGridColumnsReady || columns.length === 0) {
      return;
    }
    runWhenGridApiReady(apiRef, () => {
      try {
        apiRef.current!.pinColumn("actions", "right");
      } catch {
        // Column may not be mounted yet on first paint.
      }
    });
  }, [isGridColumnsReady, apiRef, columns]);

  // Handle selection change
  const handleSelectionChange = useCallback(
    (newSelection: any) => {
      let idsArray: any[] = [];
      let isExclude = false;

      if (Array.isArray(newSelection)) {
        idsArray = newSelection;
      } else if (newSelection && typeof newSelection === "object") {
        if (newSelection.type === "exclude") {
          isExclude = true;
        }
        if (newSelection.ids instanceof Set) {
          idsArray = Array.from(newSelection.ids);
        } else if (Array.isArray(newSelection.ids)) {
          idsArray = newSelection.ids;
        }
      }

      const mappedIds = idsArray.map((id: any) => String(id));
      const currentSelected = selectedRequirementIds || [];

      // Detect if this is a "Select All" action from the header checkbox
      let isSelectAllAction = isExclude;
      if (!isExclude && apiRef.current) {
        const pageRowIds = apiRef.current.getAllRowIds().map(String);
        // If the new selection contains all rows on the current page
        if (pageRowIds.length > 0 && pageRowIds.every(id => mappedIds.includes(id))) {
          // And we added more than 1 item in a single click (distinguishes from manually clicking the last item)
          const newlyAdded = mappedIds.filter(id => !currentSelected.includes(id));
          if (newlyAdded.length > 1) {
            isSelectAllAction = true;
          }
        }
      }

      const syncGridSelection = (ids: string[]) => {
        if (!apiRef.current) return;
        setTimeout(() => {
          apiRef.current?.setRowSelectionModel({
            type: "include",
            ids: new Set(ids),
          });
        }, 0);
      };

      // If user clicks the header checkbox when it's indeterminate (some items already selected),
      // MUI tries to "Select All", but we want to CLEAR the selection instead.
      if (isSelectAllAction && currentSelected.length > 0) {
        setSelectedIds([]);
        syncGridSelection([]);
        return;
      }

      // If it's a "Select All" from an empty state
      if (isSelectAllAction && currentSelected.length === 0) {
        let idsToSelect = mappedIds;
        if (isExclude && apiRef.current) {
          // MUI header "select all" uses exclude mode; ids are on the grid, not in the model.
          idsToSelect = apiRef.current.getAllRowIds().map(String);
        }

        if (idsToSelect.length > maxRegulationsPerBatch) {
          showMessage(
            `You can only select up to ${maxRegulationsPerBatch} regulations.`,
            "warning",
          );
        }

        const finalSelection = idsToSelect.slice(0, maxRegulationsPerBatch);
        setSelectedIds(finalSelection);
        syncGridSelection(finalSelection);
        return;
      }

      // Handle "Deselect All" action (exclude mode with all items excluded, or empty array)
      if (
        (isExclude && ((totalRows || 0) - idsArray.length === 0 || idsArray.length === totalRows)) ||
        (!isExclude && mappedIds.length === 0)
      ) {
        setSelectedIds([]);
        if (currentSelected.length > 0 && apiRef.current) {
          setTimeout(() => {
            if (apiRef.current) {
              apiRef.current.setRowSelectionModel({
                type: 'include',
                ids: new Set()
              });
            }
          }, 0);
        }
        return;
      }

      // Normal selection logic (item by item)
      if (mappedIds.length > maxRegulationsPerBatch) {
        if (mappedIds.length > currentSelected.length) {
          showMessage(`You can only select up to ${maxRegulationsPerBatch} regulations.`, "warning");
        }
        
        const prevSelected = currentSelected.filter(id => mappedIds.includes(id));
        const newSelected = mappedIds.filter(id => !currentSelected.includes(id));
        const finalSelection = [...prevSelected, ...newSelected].slice(0, maxRegulationsPerBatch);
        
        setSelectedIds(finalSelection);
        
        if (apiRef.current) {
          setTimeout(() => {
            if (apiRef.current) {
              apiRef.current.setRowSelectionModel({
                type: 'include',
                ids: new Set(finalSelection)
              });
            }
          }, 0);
        }
      } else {
        setSelectedIds(mappedIds);
      }
    },
    [setSelectedIds, maxRegulationsPerBatch, showMessage, selectedRequirementIds, apiRef, totalRows]
  );

  // Toolbar event handlers
  const handleMarketsChange = useCallback(
    (markets: ConceptValue[]) => {
      setRegulationsListMarkets(markets);
      setPaginationModel({ ...paginationModel, page: 0 });
    },
    [setRegulationsListMarkets, setPaginationModel, paginationModel]
  );
  const handleProductTypesChange = useCallback(
    (items: ConceptValue[]) => {
      setRegulationsListProductTypes(items);
      setPaginationModel({ ...paginationModel, page: 0 });
    },
    [setRegulationsListProductTypes, setPaginationModel, paginationModel]
  );
  const handleHAChange = useCallback(
    (items: ConceptValue[]) => {
      setRegulationsListHA(items);
      setPaginationModel({ ...paginationModel, page: 0 });
    },
    [setRegulationsListHA, setPaginationModel, paginationModel]
  );
  const handleTagsChange = useCallback(
    (items: ConceptValue[]) => {
      setRegulationsListTags(items);
      setPaginationModel({ ...paginationModel, page: 0 });
    },
    [setRegulationsListTags, setPaginationModel, paginationModel]
  );

  const handleTitleChange = useCallback(
    (title: string) => {
      setRegulationsListTitle(title);
      setPaginationModel({ ...paginationModel, page: 0 });
    },
    [setRegulationsListTitle, setPaginationModel, paginationModel]
  );

  const handleFavoriteFilterChange = useCallback(
    (value: "favorited" | "not_favorited" | null) => {
      setRegulationsListFavoriteFilter(value);
      setPaginationModel({ ...paginationModel, page: 0 });
    },
    [setRegulationsListFavoriteFilter, setPaginationModel, paginationModel],
  );

  const handleDateRangeChange = useCallback(
    (from: Dayjs | null, to: Dayjs | null) => {
      const isReasonableDate = (d: Dayjs | null): d is Dayjs =>
        !!d && d.isValid() && d.year() >= 1900 && d.year() <= 2100;
      setRegulationsListDateRange(
        isReasonableDate(from) ? from.toISOString() : null,
        isReasonableDate(to) ? to.toISOString() : null
      );
      setPaginationModel({ ...paginationModel, page: 0 });
    },
    [setRegulationsListDateRange, setPaginationModel, paginationModel]
  );

  // Memoize rowSelectionModel for DataGrid v8
  const gridRowSelectionModel = useMemo(() => ({
    type: 'include' as const,
    ids: new Set(selectedRequirementIds || [])
  }), [selectedRequirementIds]);

  const toolbarCompanyScopeStarting = useMemo(
    () =>
      companyScopeLoadingRegulationIds.some((id) =>
        (selectedRequirementIds ?? []).includes(id),
      ),
    [companyScopeLoadingRegulationIds, selectedRequirementIds],
  );

  return (
    <Box className={styles.gridWrapper}>
      {/* Filter Bar - placed above the table */}
      <FilterBar
        selectedMarkets={selectedMarkets}
        onMarketsChange={handleMarketsChange}
        selectedProductTypes={selectedProductTypes}
        onProductTypesChange={handleProductTypesChange}
        selectedHA={selectedHA}
        onHAChange={handleHAChange}
        selectedTags={selectedTags}
        onTagsChange={handleTagsChange}
        selectedTitle={selectedTitle}
        onTitleChange={handleTitleChange}
        fromDate={fromDate}
        toDate={toDate}
        onDateRangeChange={handleDateRangeChange}
        favoriteFilter={favoriteFilter}
        onFavoriteFilterChange={handleFavoriteFilterChange}
        initialActiveSavedFilterId={activeSavedFilterId}
        onActiveSavedFilterIdChange={setRegulationsListActiveSavedFilterId}
        trailing={
          <InsightsViewToggle
            value={landingView}
            onChange={handleLandingViewChange}
          />
        }
      />

      {/* Data Grid or Permission Denied Message */}
      {privilegesLoaded && !hasReadPermission ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px dashed #d1d5db",
            m: 2,
            minHeight: "400px",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "#374151", fontWeight: 600 }}>
            Access Denied
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 1 }}>
            You do not have permission to view the regulations table.
          </Typography>
        </Box>
      ) : (
        <Box
          className={
            landingView === "card"
              ? undefined
              : `${styles.dataGridPanel}${
                  rows.length === 0 ? ` ${styles.dataGridPanelEmpty}` : ""
                }`
          }
          sx={{
            flex: 1,
            minHeight: landingView === "card" ? 0 : 280,
            width: "100%",
            boxSizing: "border-box",
            bgcolor: landingView === "card" ? "transparent" : "background.paper",
            borderRadius: 1,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            boxShadow: "none",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {landingView === "card" ? (
            <RegulationsCardView
              rows={rows}
              loading={loading}
              totalRows={totalRows || 0}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              selectedIds={selectedRequirementIds ?? []}
              maxSelectionCount={maxRegulationsPerBatch}
              onToggleSelected={handleToggleCardSelected}
              onFavoriteChange={updateRowFavorite}
              onBulkFavoritesSuccess={handleBulkFavoritesSuccess}
              onCompanyScopeImpact={onCompanyScopeImpact}
              onCustomScopeImpact={onCustomScopeImpact}
              companyScopeStarting={toolbarCompanyScopeStarting}
            />
          ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
            {isGridColumnsReady ? (
            <DataGridPremium
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            rowHeight={80}
            // Core features
            checkboxSelection
            disableRowSelectionOnClick
            disableRowGrouping
            disableAggregation
            disablePivoting
            pagination
            paginationMode="server"
            sortingMode={favoritesListMode ? "client" : "server"}
            filterMode={favoritesListMode ? "client" : "server"}
            filterDebounceMs={400}
            localeText={REGULATIONS_GRID_FILTER_LOCALE_TEXT}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            disableColumnFilter={favoritesListMode}
            disableColumnSorting={favoritesListMode}
            rowCount={totalRows || 0}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[5, 10, 20, 50]}
            showToolbar
            showColumnVerticalBorder={false}
            disableColumnPinning={false}
            disableColumnReorder={false}
            disableColumnResize={false}
            disableMultipleRowSelection={false}
            pinnedColumnsSectionSeparator="border"
            onColumnOrderChange={handleColumnOrderChange}
            // Toolbar configuration
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            density={gridDensity}
            onDensityChange={handleDensityChange}
            slots={gridSlots}
            slotProps={{
              toolbar: {
                totalRows,
                selectedCount: selectedRequirementIds.length,
                selectedMarkets,
                selectedProductTypes,
                selectedHA,
                selectedTags,
                onMarketsChange: handleMarketsChange,
                onProductTypesChange: handleProductTypesChange,
                onHAChange: handleHAChange,
                onTagsChange: handleTagsChange,
                selectedTitle,
                onTitleChange: handleTitleChange,
                fromDate,
                toDate,
                onDateRangeChange: handleDateRangeChange,
                onCompanyScopeImpact,
                onCustomScopeImpact,
                companyScopeStarting: toolbarCompanyScopeStarting,
                onBulkFavoritesSuccess: handleBulkFavoritesSuccess,
                showGridFilterToolbarButton,
                gridFilterToolbarBadgeCount: filledGridColumnFilterCount,
              },
              filterPanel: {
                logicOperators: [GridLogicOperator.And],
                filterFormProps: {
                  filterColumns: gridFilterPanelFilterColumns,
                  logicOperatorInputProps: {
                    sx: {
                      display: "none",
                      width: 0,
                      minWidth: 0,
                      m: 0,
                      p: 0,
                      overflow: "hidden",
                    },
                  },
                },
              },
              baseCheckbox: REGULATIONS_TABLE_BASE_CHECKBOX_SLOT_PROPS,
              columnMenu: {
                sx: {
                  "& .MuiMenuItem-root": {
                    fontSize: "12px !important",
                    color: "#595959 !important",
                    padding: "4px 10px !important",
                    minHeight: "32px !important",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  },
                  "& .MuiListItemIcon-root": {
                    color: "#595959 !important",
                    minWidth: "28px !important",
                    "& .MuiSvgIcon-root": {
                      fontSize: "18px !important",
                    },
                  },
                },
              },
              panel: {
                placement: "bottom-start",
                sx: {
                  "& .MuiTypography-root:not(.regulations-filter-panel-tip)": {
                    fontSize: "14px",
                    color: "#262626",
                  },
                  "& .regulations-filter-panel-tip": {
                    fontSize: "12px",
                    color: "#a2a2a2",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: `${primaryColor} !important`,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: `${primaryColor} !important`,
                    opacity: "0.5 !important",
                  },
                  "& .MuiDataGrid-filterFormLogicOperatorInput": {
                    display: "none",
                  },
                },
              } as unknown as Partial<GridSlotProps["panel"]>,
            }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
              pinnedColumns: { right: ["actions"] },
              columns: { orderedFields: gridOrderedFields },
            }}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            rowSelectionModel={gridRowSelectionModel}
            onRowSelectionModelChange={handleSelectionChange}
            keepNonExistentRowsSelected
            sx={{
              flex: 1,
              minHeight: 0,
              height: "100%",
              border: "none",
              borderRadius: 0,
              boxShadow: "none",
              "--DataGrid-t-header-background-base": "#f9fafb",
              "--DataGrid-rowBorderColor": "transparent",
              "&.MuiDataGrid-root": {
                border: "none",
                outline: "none",
                boxShadow: "none",
              },
              "& .MuiDataGrid-scrollShadow, & .MuiDataGrid-scrollShadow--horizontal, & .MuiDataGrid-scrollShadow--vertical":
                {
                  display: "none !important",
                },
              "& .MuiDataGrid-main": {
                position: "relative",
                borderLeft: "none !important",
                borderRight: "none !important",
                outline: "none",
              },
              "& .MuiDataGrid-main::after": {
                content: '""',
                position: "absolute",
                top: "calc(var(--DataGrid-headersTotalHeight) - var(--DataGrid-columnHeaderHeight))",
                right: 0,
                width: "var(--DataGrid-scrollbarSize, 17px)",
                height: "var(--DataGrid-columnHeaderHeight)",
                backgroundColor: "#f9fafb",
                pointerEvents: "none",
                zIndex: 10,
              },
              "& .MuiDataGrid-pinnedColumns--header": {
                borderLeft: "none !important",
                borderRight: "none !important",
                boxShadow: "none !important",
              },
              "& .MuiDataGrid-columnSeparator": {
                color: "transparent",
                "&:hover": {
                  color: "#c0c0c0",
                },
              },
              "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator": {
                color: "#c0c0c0",
              },
              "& .MuiDataGrid-columnSeparator--resizing": {
                color: primaryColor,
              },
              "& .MuiDataGrid-columnHeader--dragging": {
                backgroundColor: "#f3f4f6",
              },
              "& .MuiDataGrid-topContainer": {
                borderLeft: "none !important",
                borderRight: "none !important",
                borderBottom: "none",
                boxShadow: "none !important",
              },
              "& .MuiDataGrid-topContainer::after": {
                display: "none",
              },
              "& .MuiDataGrid-toolbarContainer, & .MuiDataGrid-toolbar": {
                borderBottom: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "capitalize",
                color: "#262626",
                backgroundColor: "#f9fafb",
                borderLeft: "none !important",
                borderRight: "none !important",
                borderTop: "none !important",
                borderBottom: "none !important",
              },
              "& .MuiDataGrid-columnHeaders [role='row']": {
                backgroundColor: "#f9fafb",
              },
              "& .MuiDataGrid-columnHeadersInner": {
                backgroundColor: "#f9fafb",
                borderLeft: "none !important",
                borderRight: "none !important",
              },
              "& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-columnHeader--filler":
                {
                  backgroundColor: "#f9fafb !important",
                },
              "& .MuiDataGrid-topContainer .MuiDataGrid-filler, & .MuiDataGrid-topContainer .MuiDataGrid-scrollbarFiller":
                {
                  backgroundColor: "#f9fafb !important",
                },
              "& .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--borderTop, & .MuiDataGrid-filler--header, & .MuiDataGrid-scrollbarFiller--pinnedRight":
                {
                  backgroundColor: "#f9fafb !important",
                },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-virtualScroller > .MuiDataGrid-filler": {
                backgroundColor: "#ffffff !important",
              },
              "& .MuiDataGrid-virtualScroller > .MuiDataGrid-filler .MuiDataGrid-filler--pinnedRight, & .MuiDataGrid-virtualScroller > .MuiDataGrid-filler .MuiDataGrid-filler--pinnedLeft":
                {
                  backgroundColor: "#ffffff !important",
                },
              "& .MuiDataGrid-filler--borderBottom, & .MuiDataGrid-scrollbarFiller--borderBottom":
                {
                  borderBottom: "none !important",
                },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#f9fafb",
                borderRight: "none !important",
                borderLeft: "none !important",
                borderBottom: "none !important",
              },
              "& .MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-columnHeader--pinnedLeft":
                {
                  backgroundColor: "#f9fafb",
                  borderLeft: "none !important",
                  borderRight: "none !important",
                  boxShadow: "none",
                },
              "& .MuiDataGrid-pinnedColumnHeaders": {
                backgroundColor: "#f9fafb",
                borderLeft: "none !important",
                borderRight: "none !important",
                boxShadow: "none",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "14px",
                color: "#262626",
                fontWeight: 500,
                borderRight: "none !important",
              },
              "& .MuiDataGrid-cell--pinnedRight": {
                borderLeft: "none !important",
                boxShadow: "none",
              },
              "& .MuiDataGrid-panel": {
                "& .MuiDataGrid-columnsManagement": {
                  padding: "8px 0",
                  "& .MuiDataGrid-columnsManagementHeader": {
                    padding: "8px 16px",
                    "& .MuiTextField-root": {
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        height: "36px",
                        fontSize: "14px",
                        "& fieldset": {
                          borderColor: "#d9d9d9",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: primaryColor,
                          borderWidth: "1px",
                        },
                      },
                    },
                  },
                  "& .MuiDataGrid-columnsManagementRow": {
                    padding: "4px 16px",
                    display: "flex !important",
                    alignItems: "center",
                    "& label": {
                      display: "flex !important",
                      alignItems: "center",
                      width: "100%",
                      margin: 0,
                    },
                    "& .MuiTypography-root": {
                      display: "block !important",
                      fontSize: "14px !important",
                      color: "#262626 !important",
                      marginLeft: "8px !important",
                      visibility: "visible !important",
                      opacity: "1 !important",
                    },
                  },
                },
                "& .MuiDataGrid-columnsManagementFooter": {
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  "& .MuiFormControlLabel-root": {
                    margin: 0,
                    "& .MuiTypography-root": {
                      fontSize: "14px !important",
                      color: "#262626 !important",
                      marginLeft: "8px !important",
                    },
                  },
                },
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "#262626",
                fontSize: "14px",
              },
              "& .regulation-id-cell": {
                overflow: "visible !important",
                alignItems: "flex-start",
                lineHeight: "normal",
                whiteSpace: "normal",
                paddingTop: "8px",
                paddingBottom: "8px",
              },
              "& .impacted-concepts-cell": {
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "flex-start !important",
                "& .MuiDataGrid-cellContent": {
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                },
              },
              "& .tags-cell": {
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "flex-start !important",
                "& .MuiDataGrid-cellContent": {
                  height: "100%",
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                },
              },
              "& .synopsis-cell": {
                display: "flex !important",
                alignItems: "center !important",
                "& .MuiDataGrid-cellContent": {
                  height: "100%",
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                },
              },
            }}
          />
            ) : null}
          </LocalizationProvider>
          )}
        </Box>
      )}
    </Box>
  );
};

export default RegulationsTable;
